/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { DesktopRouteState } from '@poweralpha/common';
import {
  createHistory,
  type DesktopState,
  type DesktopTabState,
  type DesktopWindowAction,
  type DesktopWindowState,
} from './windowSystem';

export const MAX_APP_TABS = 20;

/** Legacy sessions keep their existing draft scope as the first tab. */
export const getWindowTabs = <T extends DesktopRouteState>(
  window: Pick<DesktopWindowState<T>, 'id' | 'tabs' | 'path' | 'history' | 'routeState'>,
): DesktopTabState<T>[] =>
  window.tabs ?? [
    { id: window.id, path: window.path, history: window.history, routeState: window.routeState },
  ];

/** Window location is the selected tab's projection, used by shell and Agent navigation. */
export function syncSelectedTab<T extends DesktopRouteState>(
  window: DesktopWindowState<T>,
  updates: Partial<DesktopWindowState<T>>,
  tabId = window.selectedTabId ?? window.id,
): DesktopWindowState<T> {
  const tabs = getWindowTabs(window).map((tab) =>
    tab.id === tabId
      ? {
          ...tab,
          ...(updates.path !== undefined ? { path: updates.path } : {}),
          ...(updates.history !== undefined ? { history: updates.history } : {}),
          ...('routeState' in updates ? { routeState: updates.routeState } : {}),
        }
      : tab,
  );
  const selected = tabs.find(({ id }) => id === window.selectedTabId) ?? tabs[0];
  return {
    ...window,
    ...updates,
    tabs,
    selectedTabId: selected.id,
    path: selected.path,
    history: selected.history,
    routeState: selected.routeState,
  };
}

type TabAction<T extends DesktopRouteState> = Extract<
  DesktopWindowAction<T>,
  { type: 'create_tab' | 'select_tab' | 'close_tab' }
>;

function transitionTabs<T extends DesktopRouteState>(
  currentTabs: DesktopTabState<T>[],
  currentSelectedTabId: string | undefined,
  windowId: string,
  action: TabAction<T>,
) {
  let tabs = currentTabs;
  let selectedTabId = currentSelectedTabId ?? tabs[0].id;
  switch (action.type) {
    case 'create_tab':
      {
        if (tabs.length >= MAX_APP_TABS) return undefined;
        const id = action.tabId ?? `${windowId}:tab:${crypto.randomUUID()}`;
        if (tabs.some((tab) => tab.id === id)) return undefined;
        tabs = [
          ...tabs,
          { id, path: action.path, history: createHistory(action.path, action.state) },
        ];
        selectedTabId = id;
      }
      break;
    case 'select_tab':
      {
        if (!tabs.some(({ id }) => id === action.tabId)) return undefined;
        selectedTabId = action.tabId;
      }
      break;
    case 'close_tab':
      {
        const index = tabs.findIndex(({ id }) => id === action.tabId);
        if (index === -1 || tabs.length === 1) return undefined;
        tabs = tabs.filter(({ id }) => id !== action.tabId);
        if (selectedTabId === action.tabId)
          selectedTabId = tabs[Math.min(index, tabs.length - 1)].id;
      }
      break;
    default:
      return undefined;
  }

  return { tabs, selectedTabId };
}

export function applyTabAction<T extends DesktopRouteState>(
  state: DesktopState<T>,
  action: TabAction<T>,
): DesktopState<T> {
  const target = state.windows.find(({ id }) => id === action.id);
  if (!target) return state;
  const transition = transitionTabs(getWindowTabs(target), target.selectedTabId, target.id, action);
  if (!transition) return state;
  const { tabs, selectedTabId } = transition;
  const selected = tabs.find(({ id }) => id === selectedTabId) ?? tabs[0];
  return {
    ...state,
    focusedWindowId: target.id,
    nextZIndex: state.nextZIndex + 1,
    windows: state.windows.map((window) =>
      window.id === target.id
        ? {
            ...window,
            tabs,
            selectedTabId,
            path: selected.path,
            history: selected.history,
            routeState: selected.routeState,
            zIndex: state.nextZIndex,
            updatedAt: new Date().toISOString(),
          }
        : window,
    ),
  };
}
